import alphaVantageService from './alphaVantageService';
import { supabaseService } from './supabase';
import cacheService from './cacheService';
import {
  FinancialData,
  PricePoint,
  TradingSignal,
  CapeScore,
  FScore,
  PnFChart,
  PnFColumn,
  PnFPattern,
  SignalAction
} from '../types';

// Configuration for the trading strategy
const CONFIG = {
  capeLookbackYears: 8,
  capeEmaSpan: 4,
  pnfBoxSizePercent: 0.01, // 1%
  pnfReversalBoxes: 3,
  pnfPatternType: 'quad-top', // 'triple-top' or 'quad-top'
};

/**
 * Calculate Cyclically Adjusted Price-to-Earnings (CAPE) ratio
 * @param ticker Stock ticker symbol
 * @param financialData Financial data for the ticker
 */
export const calculateCAPE = async (
  ticker: string,
  financialData?: FinancialData
): Promise<CapeScore> => {
  try {
    // Use provided financial data or fetch it
    const data = financialData || await alphaVantageService.getFinancialData(ticker);
    
    // Get current price (most recent closing price)
    const currentPrice = data.priceHistory.length > 0 
      ? data.priceHistory[0].close 
      : await alphaVantageService.getCurrentPrice(ticker);
    
    // Sort income statements by date (most recent first)
    const sortedIncome = [...data.incomeStatement].sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    
    // Calculate average earnings over lookback period
    const requiredQuarters = CONFIG.capeLookbackYears * 4;
    
    if (sortedIncome.length < requiredQuarters) {
      console.warn(`Insufficient data for ${ticker} CAPE calculation. Need ${requiredQuarters} quarters, have ${sortedIncome.length}.`);
      return {
        ticker,
        cape: 0,
        capePercentile: 50, // Neutral if we can't calculate
        date: new Date().toISOString().split('T')[0]
      };
    }
    
    // Take the last N quarters of earnings
    const earningsHistory = sortedIncome
      .slice(0, requiredQuarters)
      .map(stmt => parseFloat(stmt.netIncome.toString()));
    
    // Calculate EMA of earnings
    const emaEarnings = calculateEMA(earningsHistory, CONFIG.capeEmaSpan);
    
    // Calculate CAPE ratio
    const cape = emaEarnings > 0 ? currentPrice / emaEarnings : 0;
    
    // We calculate percentile after collecting all CAPEs
    return {
      ticker,
      cape,
      capePercentile: 50, // Placeholder, will be updated later
      date: new Date().toISOString().split('T')[0]
    };
  } catch (error) {
    console.error(`Error calculating CAPE for ${ticker}:`, error);
    return {
      ticker,
      cape: 0,
      capePercentile: 50,
      date: new Date().toISOString().split('T')[0]
    };
  }
};

/**
 * Calculate Piotroski F-Score for a ticker
 * @param ticker Stock ticker symbol
 * @param financialData Financial data for the ticker
 */
export const calculateFScore = async (
  ticker: string,
  financialData?: FinancialData
): Promise<FScore> => {
  try {
    // Use provided financial data or fetch it
    const data = financialData || await alphaVantageService.getFinancialData(ticker);
    
    // Sort statements by date (most recent first)
    const sortedIncome = [...data.incomeStatement].sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    
    const sortedBalance = [...data.balanceSheet].sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    
    const sortedCashFlow = [...data.cashFlow].sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    
    // Need at least 2 years of data for some calculations
    if (sortedIncome.length < 2 || sortedBalance.length < 2 || sortedCashFlow.length < 1) {
      console.warn(`Insufficient data for ${ticker} F-Score calculation`);
      return {
        ticker,
        score: 0,
        roa: false,
        cfo: false,
        deltaRoa: false,
        accrual: false,
        deltaLeverage: false,
        deltaLiquidity: false,
        noEquityRaise: false,
        deltaMargin: false,
        deltaTurnover: false
      };
    }
    
    // Get latest and previous year data
    const latestIncome = sortedIncome[0];
    const prevIncome = sortedIncome[1];
    
    const latestBalance = sortedBalance[0];
    const prevBalance = sortedBalance[1];
    
    const latestCashFlow = sortedCashFlow[0];
    
    // 1. Return on Assets (ROA) - Positive means 1 point
    const roa = parseFloat(latestIncome.netIncome.toString()) / parseFloat(latestBalance.totalAssets.toString());
    const fRoa = roa > 0;
    
    // 2. Operating Cash Flow - Positive means 1 point
    const operatingCashFlow = parseFloat(latestCashFlow.operatingCashFlow.toString());
    const fCfo = operatingCashFlow > 0;
    
    // 3. Change in ROA - Increase means 1 point
    const prevRoa = parseFloat(prevIncome.netIncome.toString()) / parseFloat(prevBalance.totalAssets.toString());
    const fDeltaRoa = roa > prevRoa;
    
    // 4. Accruals (CFO > ROA) - True means 1 point
    const cfRoa = operatingCashFlow / parseFloat(latestBalance.totalAssets.toString());
    const fAccrual = cfRoa > roa;
    
    // 5. Change in Leverage - Decrease means 1 point
    const leverage = parseFloat(latestBalance.totalDebt?.toString() || '0') / parseFloat(latestBalance.totalAssets.toString());
    const prevLeverage = parseFloat(prevBalance.totalDebt?.toString() || '0') / parseFloat(prevBalance.totalAssets.toString());
    const fLeverage = leverage < prevLeverage;
    
    // 6. Change in Current Ratio - Increase means 1 point
    const currentRatio = parseFloat(latestBalance.currentAssets.toString()) / parseFloat(latestBalance.currentLiabilities.toString());
    const prevCurrentRatio = parseFloat(prevBalance.currentAssets.toString()) / parseFloat(prevBalance.currentLiabilities.toString());
    const fLiquidity = currentRatio > prevCurrentRatio;
    
    // 7. Change in Shares Outstanding - No new shares means 1 point
    const sharesOutstanding = parseFloat(latestBalance.sharesOutstanding?.toString() || '0');
    const prevSharesOutstanding = parseFloat(prevBalance.sharesOutstanding?.toString() || '0');
    const fEquityOffering = sharesOutstanding <= prevSharesOutstanding;
    
    // 8. Change in Gross Margin - Increase means 1 point
    const grossMargin = parseFloat(latestIncome.grossProfit.toString()) / parseFloat(latestIncome.totalRevenue.toString());
    const prevGrossMargin = parseFloat(prevIncome.grossProfit.toString()) / parseFloat(prevIncome.totalRevenue.toString());
    const fMargin = grossMargin > prevGrossMargin;
    
    // 9. Change in Asset Turnover - Increase means 1 point
    const assetTurnover = parseFloat(latestIncome.totalRevenue.toString()) / parseFloat(latestBalance.totalAssets.toString());
    const prevAssetTurnover = parseFloat(prevIncome.totalRevenue.toString()) / parseFloat(prevBalance.totalAssets.toString());
    const fTurnover = assetTurnover > prevAssetTurnover;
    
    // Calculate total F-Score (sum of all 9 binary signals)
    const score = (fRoa ? 1 : 0) + 
                  (fCfo ? 1 : 0) + 
                  (fDeltaRoa ? 1 : 0) + 
                  (fAccrual ? 1 : 0) + 
                  (fLeverage ? 1 : 0) + 
                  (fLiquidity ? 1 : 0) + 
                  (fEquityOffering ? 1 : 0) + 
                  (fMargin ? 1 : 0) + 
                  (fTurnover ? 1 : 0);
    
    return {
      ticker,
      score,
      roa: fRoa,
      cfo: fCfo,
      deltaRoa: fDeltaRoa,
      accrual: fAccrual,
      deltaLeverage: fLeverage,
      deltaLiquidity: fLiquidity,
      noEquityRaise: fEquityOffering,
      deltaMargin: fMargin,
      deltaTurnover: fTurnover
    };
  } catch (error) {
    console.error(`Error calculating F-Score for ${ticker}:`, error);
    return {
      ticker,
      score: 0,
      roa: false,
      cfo: false,
      deltaRoa: false,
      accrual: false,
      deltaLeverage: false,
      deltaLiquidity: false,
      noEquityRaise: false,
      deltaMargin: false,
      deltaTurnover: false
    };
  }
};

/**
 * Generate Point & Figure Chart from price data
 * @param ticker Stock ticker symbol
 * @param priceData Array of price points
 */
export const generatePnFChart = (
  ticker: string,
  priceData: PricePoint[]
): PnFChart | null => {
  try {
    if (priceData.length < 20) {
      console.warn(`Insufficient price data for ${ticker} P&F chart (need at least 20 data points)`);
      return null;
    }
    
    // Sort price data by date (newest first)
    const sortedPrices = [...priceData].sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    
    // Extract closing prices
    const prices = sortedPrices.map(p => p.close);
    
    // Get current price and calculate box size
    const currentPrice = prices[0];
    const boxSize = currentPrice * CONFIG.pnfBoxSizePercent;
    const reversalSize = CONFIG.pnfReversalBoxes * boxSize;
    
    // Initialize P&F chart
    const pnfChart: PnFChart = {
      ticker,
      columns: [],
      currentPrice,
      patterns: {
        tripleTopBreakout: false,
        tripleTopBreakdown: false,
        quadTopBreakout: false,
        quadTopBreakdown: false
      }
    };
    
    // Initialize first column with the first price
    let currentDirection: 'up' | 'down' = 'up';
    let currentColumn: PnFColumn = {
      direction: currentDirection,
      boxes: [prices[prices.length - 1]], // Start with oldest price
      high: prices[prices.length - 1],
      low: prices[prices.length - 1]
    };
    
    pnfChart.columns.push(currentColumn);
    
    // Process each price to build the P&F chart
    for (let i = prices.length - 2; i >= 0; i--) {
      const price = prices[i];
      currentColumn = pnfChart.columns[pnfChart.columns.length - 1];
      
      if (currentDirection === 'up') {
        // In an up column
        if (price >= currentColumn.high + boxSize) {
          // Price moved up by at least one box
          const newBoxes = Math.floor((price - currentColumn.high) / boxSize);
          for (let j = 0; j < newBoxes; j++) {
            const newPrice = currentColumn.high + boxSize;
            currentColumn.boxes.push(newPrice);
            currentColumn.high = newPrice;
          }
        } else if (price <= currentColumn.high - reversalSize) {
          // Reversal down by at least 'reversalSize'
          // Start a new column
          currentDirection = 'down';
          
          // Calculate how many boxes down
          const startPrice = currentColumn.high;
          const newBoxes = Math.floor((startPrice - price) / boxSize);
          
          const newColumn: PnFColumn = {
            direction: 'down',
            boxes: [],
            high: startPrice,
            low: startPrice
          };
          
          // Add boxes
          for (let j = 0; j < newBoxes; j++) {
            const newPrice = startPrice - (j + 1) * boxSize;
            newColumn.boxes.push(newPrice);
            newColumn.low = newPrice;
          }
          
          pnfChart.columns.push(newColumn);
        }
      } else {
        // In a down column
        if (price <= currentColumn.low - boxSize) {
          // Price moved down by at least one box
          const newBoxes = Math.floor((currentColumn.low - price) / boxSize);
          for (let j = 0; j < newBoxes; j++) {
            const newPrice = currentColumn.low - boxSize;
            currentColumn.boxes.push(newPrice);
            currentColumn.low = newPrice;
          }
        } else if (price >= currentColumn.low + reversalSize) {
          // Reversal up by at least 'reversalSize'
          // Start a new column
          currentDirection = 'up';
          
          // Calculate how many boxes up
          const startPrice = currentColumn.low;
          const newBoxes = Math.floor((price - startPrice) / boxSize);
          
          const newColumn: PnFColumn = {
            direction: 'up',
            boxes: [],
            high: startPrice,
            low: startPrice
          };
          
          // Add boxes
          for (let j = 0; j < newBoxes; j++) {
            const newPrice = startPrice + (j + 1) * boxSize;
            newColumn.boxes.push(newPrice);
            newColumn.high = newPrice;
          }
          
          pnfChart.columns.push(newColumn);
        }
      }
    }
    
    // Identify patterns
    identifyPnFPatterns(pnfChart);
    
    return pnfChart;
  } catch (error) {
    console.error(`Error generating P&F chart for ${ticker}:`, error);
    return null;
  }
};

/**
 * Identify Triple/Quad-Top breakout and breakdown patterns
 * @param pnfChart Point & Figure chart
 */
function identifyPnFPatterns(pnfChart: PnFChart): void {
  const columns = pnfChart.columns;
  const requiredTops = CONFIG.pnfPatternType === 'triple-top' ? 3 : 4;
  
  if (columns.length < requiredTops + 1) {
    // Need at least requiredTops + 1 columns for a proper pattern
    return;
  }
  
  // Extract tops and bottoms
  const tops: { index: number, value: number }[] = [];
  const bottoms: { index: number, value: number }[] = [];
  
  columns.forEach((col, i) => {
    if (col.direction === 'up') {
      tops.push({ index: i, value: col.high });
    } else {
      bottoms.push({ index: i, value: col.low });
    }
  });
  
  // Check for Triple/Quad-Top Breakout
  if (tops.length >= requiredTops) {
    for (let i = 0; i <= tops.length - requiredTops; i++) {
      const equalTops = requiredTops === 3
        ? tops[i].value === tops[i+1].value && tops[i+1].value === tops[i+2].value
        : tops[i].value === tops[i+1].value && tops[i+1].value === tops[i+2].value && tops[i+2].value === tops[i+3].value;
      
      const breakout = i + requiredTops < tops.length && tops[i + requiredTops].value > tops[i].value;
      
      if (equalTops && breakout) {
        if (requiredTops === 3) {
          pnfChart.patterns.tripleTopBreakout = true;
        } else {
          pnfChart.patterns.quadTopBreakout = true;
        }
        break;
      }
    }
  }
  
  // Check for Triple/Quad-Top Breakdown
  if (bottoms.length >= requiredTops) {
    for (let i = 0; i <= bottoms.length - requiredTops; i++) {
      const equalBottoms = requiredTops === 3
        ? bottoms[i].value === bottoms[i+1].value && bottoms[i+1].value === bottoms[i+2].value
        : bottoms[i].value === bottoms[i+1].value && bottoms[i+1].value === bottoms[i+2].value && bottoms[i+2].value === bottoms[i+3].value;
      
      const breakdown = i + requiredTops < bottoms.length && bottoms[i + requiredTops].value < bottoms[i].value;
      
      if (equalBottoms && breakdown) {
        if (requiredTops === 3) {
          pnfChart.patterns.tripleTopBreakdown = true;
        } else {
          pnfChart.patterns.quadTopBreakdown = true;
        }
        break;
      }
    }
  }
}

/**
 * Helper function to calculate exponential moving average
 * @param data Array of numbers
 * @param span EMA span
 */
function calculateEMA(data: number[], span: number): number {
  if (data.length === 0) return 0;
  
  const alpha = 2 / (span + 1);
  let ema = data[0];
  
  for (let i = 1; i < data.length; i++) {
    ema = alpha * data[i] + (1 - alpha) * ema;
  }
  
  return ema;
}

/**
 * Generate trading signal based on the Triple Point strategy
 * @param ticker Stock symbol
 * @param financialData Optional financial data for performance
 */
export const generateTradingSignal = async (
  ticker: string,
  financialData?: FinancialData
): Promise<TradingSignal> => {
  try {
    // Check if we have a cached signal
    const cacheKey = `trading_signal_${ticker}`;
    const cachedSignal = await cacheService.getCachedData(
      cacheKey,
      async () => {
        // Fetch financial data if not provided
        const data = financialData || await alphaVantageService.getFinancialData(ticker);
        
        // Calculate CAPE score
        const capeScore = await calculateCAPE(ticker, data);
        
        // Calculate F-Score
        const fScore = await calculateFScore(ticker, data);
        
        // Generate P&F chart
        const pnfChart = generatePnFChart(ticker, data.priceHistory);
        
        // Determine preliminary signal based on CAPE and F-Score
        let preliminarySignal: SignalAction = 'Hold';
        
        if (capeScore.capePercentile <= 20 && fScore.score >= 6) {
          preliminarySignal = 'Buy';
        } else if (capeScore.capePercentile >= 80 && fScore.score <= 4) {
          preliminarySignal = 'Sell';
        }
        
        // Determine final signal based on P&F confirmation
        let finalSignal: SignalAction = 'Hold';
        
        if (preliminarySignal === 'Buy' && pnfChart?.patterns[CONFIG.pnfPatternType === 'triple-top' ? 'tripleTopBreakout' : 'quadTopBreakout']) {
          finalSignal = 'Buy';
        } else if (preliminarySignal === 'Sell' && pnfChart?.patterns[CONFIG.pnfPatternType === 'triple-top' ? 'tripleTopBreakdown' : 'quadTopBreakdown']) {
          finalSignal = 'Sell';
        }
        
        // Create the trading signal
        const tradingSignal: TradingSignal = {
          ticker,
          cape: capeScore.cape,
          capePercentile: capeScore.capePercentile,
          f_score: fScore.score,
          preliminarySignal,
          finalSignal,
          date: new Date().toISOString().split('T')[0]
        };
        
        // Store in Supabase if available
        try {
          await supabaseService.tradingSignals.saveSignal(tradingSignal);
        } catch (err) {
          console.warn('Could not save signal to database:', err);
        }
        
        return tradingSignal;
      },
      {
        ttl: 24 * 60 * 60 * 1000, // Cache for 24 hours
        forceRefresh: false
      }
    );
    
    return cachedSignal;
  } catch (error) {
    console.error(`Error generating trading signal for ${ticker}:`, error);
    return {
      ticker,
      cape: 0,
      capePercentile: 50,
      f_score: 0,
      preliminarySignal: 'Hold' as SignalAction,
      finalSignal: 'Hold' as SignalAction,
      date: new Date().toISOString().split('T')[0]
    };
  }
};

/**
 * Calculate CAPE percentiles for a list of stocks
 * @param capeScores Array of CAPE scores without percentiles
 */
export const calculateCAPEPercentiles = (capeScores: CapeScore[]): CapeScore[] => {
  // Sort by CAPE value
  const sortedScores = [...capeScores].sort((a, b) => a.cape - b.cape);
  
  // Calculate percentiles
  const n = sortedScores.length;
  const result = sortedScores.map((score, i) => ({
    ...score,
    capePercentile: (i / (n - 1)) * 100
  }));
  
  return result;
};

/**
 * Generate trading signals for all S&P 500 stocks
 * This is a computationally expensive operation, so it's cached
 * @param forceRefresh Force a refresh of the signals, bypassing the cache
 */
export const generateAllTradingSignals = async (forceRefresh: boolean = false): Promise<TradingSignal[]> => {
  try {
    console.log('Generating all trading signals with forceRefresh:', forceRefresh);
    
    // Get signals from cache or compute them
    return await cacheService.getCachedData(
      'all_trading_signals',
      async () => {
        // Get all tickers from Alpha Vantage
        console.log('Fetching tickers from Alpha Vantage API...');
        const tickers = await alphaVantageService.getSP500Tickers();
        console.log(`Fetched ${tickers.length} tickers from Alpha Vantage API`);
        
        // Start with a smaller subset if more than 500 tickers returned
        // to avoid exceeding API rate limits
        let tickersToProcess = tickers;
        if (tickers.length > 200) {
          tickersToProcess = tickers.slice(0, 200); // Limit to 200 tickers for bulk processing
        }
        
        console.log(`Generating trading signals for ${tickersToProcess.length} stocks...`);
        
        // First, calculate CAPE for all tickers
        console.log('Calculating CAPE scores...');
        const capePromises = tickersToProcess.map((ticker: string) => calculateCAPE(ticker));
        const capeScores = await Promise.all(capePromises);
        console.log(`Calculated ${capeScores.length} CAPE scores`);
        
        // Calculate percentiles
        const capeWithPercentiles = calculateCAPEPercentiles(capeScores);
        
        // Create a map for easier lookup
        const capeMap = new Map<string, CapeScore>();
        capeWithPercentiles.forEach(score => {
          capeMap.set(score.ticker, score);
        });
        
        // Generate signals for each ticker
        console.log('Generating signals for each ticker...');
        const signalPromises = tickersToProcess.map(async (ticker: string) => {
          try {
            // Get financial data
            const data = await alphaVantageService.getFinancialData(ticker);
            
            // Calculate F-Score
            const fScore = await calculateFScore(ticker, data);
            
            // Generate P&F chart
            const pnfChart = generatePnFChart(ticker, data.priceHistory);
            
            // Get CAPE score with percentile
            const capeScore = capeMap.get(ticker) || {
              ticker,
              cape: 0,
              capePercentile: 50,
              date: new Date().toISOString().split('T')[0]
            };
            
            // Determine preliminary signal
            let preliminarySignal: SignalAction = 'Hold';
            
            if (capeScore.capePercentile <= 20 && fScore.score >= 6) {
              preliminarySignal = 'Buy';
            } else if (capeScore.capePercentile >= 80 && fScore.score <= 4) {
              preliminarySignal = 'Sell';
            }
            
            // Determine final signal
            let finalSignal: SignalAction = 'Hold';
            
            if (preliminarySignal === 'Buy' && pnfChart?.patterns[CONFIG.pnfPatternType === 'triple-top' ? 'tripleTopBreakout' : 'quadTopBreakout']) {
              finalSignal = 'Buy';
            } else if (preliminarySignal === 'Sell' && pnfChart?.patterns[CONFIG.pnfPatternType === 'triple-top' ? 'tripleTopBreakdown' : 'quadTopBreakdown']) {
              finalSignal = 'Sell';
            }
            
            return {
              ticker,
              cape: capeScore.cape,
              capePercentile: capeScore.capePercentile,
              f_score: fScore.score,
              preliminarySignal: preliminarySignal as SignalAction,
              finalSignal: finalSignal as SignalAction,
              date: new Date().toISOString().split('T')[0]
            };
          } catch (error) {
            console.error(`Error generating signal for ${ticker}:`, error);
            return {
              ticker,
              cape: 0,
              capePercentile: 50,
              f_score: 0,
              preliminarySignal: 'Hold' as SignalAction,
              finalSignal: 'Hold' as SignalAction,
              date: new Date().toISOString().split('T')[0]
            };
          }
        });
        
        // Wait for all signals to be generated
        const signals = await Promise.all(signalPromises);
        console.log(`Generated ${signals.length} signals total`);
        console.log(`Buy signals: ${signals.filter(s => s.finalSignal === 'Buy').length}`);
        console.log(`Sell signals: ${signals.filter(s => s.finalSignal === 'Sell').length}`);
        console.log(`Hold signals: ${signals.filter(s => s.finalSignal === 'Hold').length}`);
        
        // Store all signals in Supabase if available
        try {
          for (const signal of signals) {
            await supabaseService.tradingSignals.saveSignal(signal);
          }
        } catch (err) {
          console.warn('Could not save signals to database:', err);
        }
        
        return signals;
      },
      {
        ttl: 24 * 60 * 60 * 1000, // Cache for 24 hours
        forceRefresh: forceRefresh
      }
    );
  } catch (error) {
    console.error('Error generating all trading signals:', error);
    return [];
  }
};

/**
 * Get Buy signals based on the Triple Point strategy
 */
export const getBuySignals = async (): Promise<TradingSignal[]> => {
  try {
    const signals = await generateAllTradingSignals();
    return signals.filter(signal => signal.finalSignal === 'Buy');
  } catch (error) {
    console.error('Error fetching buy signals:', error);
    return [];
  }
};

/**
 * Get Sell signals based on the Triple Point strategy
 */
export const getSellSignals = async (): Promise<TradingSignal[]> => {
  try {
    const signals = await generateAllTradingSignals();
    return signals.filter(signal => signal.finalSignal === 'Sell');
  } catch (error) {
    console.error('Error fetching sell signals:', error);
    return [];
  }
};

// Export the trading strategy service
export const tradeStrategyService = {
  calculateCAPE,
  calculateFScore,
  generatePnFChart,
  generateTradingSignal,
  generateAllTradingSignals: (forceRefresh: boolean = false) => generateAllTradingSignals(forceRefresh),
  getBuySignals,
  getSellSignals,
  calculateCAPEPercentiles
};

export default tradeStrategyService; 